import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { TanStackTableRoot } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useBrandsAndProducts } from '~/react-query-hooks/knowledge-graph/useBrandsAndProducts';

import { LoaderCell } from '../../theme-exposure-tab/theme-exposure-table/loader-cell/LoaderCell';
import { RankingMethodologySection } from '../../theme-exposure-tab/theme-exposure-table/ranking-methodology-section/RankingMethodologySection';
import { useBrandAndProductDrawer } from '../useBrandAndProductDrawer';
import * as S from './BrandsAndProductsTable.styles';
import { getColumns, RANK_COLUMN_ID } from './utils/getColumns';

interface BrandsAndProductsTableProps {
  entity: Entity;
}

export const BrandsAndProductsTable = ({
  entity,
}: BrandsAndProductsTableProps) => {
  const { t } = useTranslation(['widget']);

  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isFetching, error } = useBrandsAndProducts(entity.tag);
  const { openDrawer } = useBrandAndProductDrawer();

  const columns = getColumns({ t });

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (error) {
    throw error;
  }

  return (
    <>
      <S.TableWrapper ref={containerRef} $isLoading={isFetching}>
        <TanStackTableRoot data-testid="brands-and-products-table">
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  return (
                    <S.StyledTableHeader
                      key={header.id}
                      alignment="left"
                      isHoverEnabled={false}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                    </S.StyledTableHeader>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {isFetching ? (
              <LoaderCell numberOfRows={3} />
            ) : (
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => openDrawer(row.original.product)}
                >
                  {row.getVisibleCells().map(cell => {
                    return (
                      <S.StyledTableCell
                        key={cell.id}
                        alignment="left"
                        isNumber={cell.column.id === RANK_COLUMN_ID}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </S.StyledTableCell>
                    );
                  })}
                </tr>
              ))
            )}
            <tr data-testid="footer-row">
              <S.FooterRow
                colspan={table.getHeaderGroups()[0].headers.length}
                isNumber={false}
              >
                <RankingMethodologySection rankingType="companyToBrandAndProduct" />
              </S.FooterRow>
            </tr>
          </tbody>
        </TanStackTableRoot>
      </S.TableWrapper>
    </>
  );
};
